import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Building2,
  Eye,
  EyeOff,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { venuesApi, getStoredAuth, resolveMediaUrl } from "@/lib/api";
import { toast } from "sonner";

export const Route = createFileRoute("/_host/my-venues")({
  head: () => ({
    meta: [{ title: "My Venues — OyoBooking" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: MyVenuesPage,
  beforeLoad: ({ location }) => {
    if (typeof window === "undefined") return;
    const auth = getStoredAuth();
    if (!auth) throw redirect({ to: "/login", search: { redirect: location.pathname } });
    const HOST_ROLES = [
      "venue_manager", "hotel_owner", "event_centre_owner", "open_space_owner",
      "resort_owner", "hotel_event_owner", "apartment_owner", "conference_hall_owner",
    ] as const;
    if (!auth.role || !(HOST_ROLES as readonly string[]).includes(auth.role)) {
      throw redirect({ to: "/" });
    }
  },
});

interface Venue {
  id: string;
  name: string;
  type: string;
  location: string;
  description?: string | null;
  basePrice: number;
  status: string;
  moderationStatus?: string | null;
  isAvailable: boolean;
  heroImage?: string | null;
  roomTypes?: { id: string; name: string; pricePerDay: number }[];
}

const getPriceLabel = (type: string) =>
  ["Conference Hall", "Event Center", "Open Space"].includes(type)
    ? "Hire Price"
    : type === "Hotel"
      ? "Starting Room Price"
      : "Nightly Price";

const getPriceSuffix = (type: string) =>
  ["Conference Hall", "Event Center", "Open Space"].includes(type) ? "/event" : "/night";

const getPriceHelpText = (type: string) => {
  if (type === "Hotel") return "This is the listing's starting display price. Edit individual room prices from Room Types.";
  if (type === "Resort") return "Set the starting nightly rate guests will see for this resort.";
  if (type === "Apartment") return "Set the nightly rate guests will pay for this apartment.";
  return "Set the hire price guests will see for this listing.";
};

const getPriceStep = (type: string) =>
  ["Conference Hall", "Event Center", "Open Space"].includes(type) ? 50_000 : 5_000;

function MyVenuesPage() {
  const { user } = useAuth();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [editVenue, setEditVenue] = useState<Venue | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    loadVenues();
  }, [user?.id]);

  async function loadVenues() {
    setLoading(true);
    try {
      const res = await venuesApi.myVenues(user!.id);
      setVenues(res.data ?? []);
    } catch {
      toast.error("Failed to load venues");
    } finally {
      setLoading(false);
    }
  }

  async function toggleAvailability(v: Venue) {
    try {
      await venuesApi.update(v.id, { isAvailable: !v.isAvailable });
      setVenues((prev) =>
        prev.map((x) => (x.id === v.id ? { ...x, isAvailable: !x.isAvailable } : x)),
      );
      toast.success(`Venue is now ${!v.isAvailable ? "available" : "unavailable"}`);
    } catch {
      toast.error("Failed to update availability");
    }
  }

  async function saveEdit() {
    if (!editVenue) return;

    const basePrice = Number(editVenue.basePrice);
    if (!Number.isFinite(basePrice) || basePrice <= 0) {
      toast.error(`Enter a valid ${getPriceLabel(editVenue.type).toLowerCase()} greater than zero`);
      return;
    }
    if (!editVenue.name.trim()) {
      toast.error("Venue name is required");
      return;
    }
    if (!editVenue.location.trim()) {
      toast.error("Location is required");
      return;
    }

    try {
      await venuesApi.update(editVenue.id, {
        name: editVenue.name.trim(),
        location: editVenue.location.trim(),
        description: editVenue.description?.trim() || undefined,
        basePrice,
      });
      setVenues((prev) =>
        prev.map((x) => (x.id === editVenue.id ? { ...editVenue, basePrice } : x)),
      );
      setEditVenue(null);
      toast.success("Venue updated");
    } catch (err: any) {
      const message = err?.response?.data?.title || err?.message || "Failed to update venue";
      toast.error(message);
    }
  }

  async function confirmDelete() {
    if (!deleteId) return;
    try {
      await venuesApi.delete(deleteId);
      setVenues((prev) => prev.filter((x) => x.id !== deleteId));
      setDeleteId(null);
      toast.success("Venue deleted");
    } catch {
      toast.error("Failed to delete venue");
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Venues</h1>
          <Button asChild>
            <a href="/list-your-property" aria-label="Add new venue">
              <Plus className="w-4 h-4 mr-2" />
              Add Venue
            </a>
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : venues.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border">
            <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">You haven't listed any venues yet.</p>
            <Button asChild>
              <a href="/list-your-property">List Your Property</a>
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {venues.map((v) => (
              <div
                key={v.id}
                className="bg-white rounded-xl border shadow-sm overflow-hidden"
              >
                <div className="h-40 bg-gray-100 relative">
                  {v.heroImage ? (
                    <img
                      src={resolveMediaUrl(v.heroImage)}
                      alt={v.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-300">
                      <Building2 className="w-10 h-10" />
                    </div>
                  )}
                  <span
                    className={`absolute top-2 right-2 text-xs px-2 py-1 rounded-full ${
                      v.moderationStatus === "approved"
                        ? "bg-green-100 text-green-700"
                        : v.moderationStatus === "rejected"
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                    aria-label={`Moderation status: ${v.moderationStatus}`}
                  >
                    {v.moderationStatus}
                  </span>
                </div>
                <div className="p-4">
                  <h2 className="font-semibold text-gray-900 truncate" title={v.name}>
                    {v.name}
                  </h2>
                  <div className="flex items-center text-sm text-gray-500 mt-1">
                    <MapPin className="w-3 h-3 mr-1" />
                    <span className="truncate">{v.location}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm font-medium text-blue-600">
                      ₦{v.basePrice.toLocaleString()}{getPriceSuffix(v.type)}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleAvailability(v)}
                        aria-label={v.isAvailable ? "Make unavailable" : "Make available"}
                        title={v.isAvailable ? "Make unavailable" : "Make available"}
                      >
                        {v.isAvailable ? (
                          <Eye className="w-4 h-4 text-green-600" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditVenue(v)}
                        aria-label="Edit venue"
                        title="Edit venue"
                      >
                        <Pencil className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(v.id)}
                        aria-label="Delete venue"
                        title="Delete venue"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editVenue} onOpenChange={(open) => !open && setEditVenue(null)}>
        <DialogContent aria-describedby="edit-venue-desc">
          <DialogHeader>
            <DialogTitle>Edit Venue</DialogTitle>
            <DialogDescription id="edit-venue-desc">
              Update your venue details.
            </DialogDescription>
          </DialogHeader>
          {editVenue && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="venue-name">Name</Label>
                <Input
                  id="venue-name"
                  value={editVenue.name}
                  onChange={(e) =>
                    setEditVenue({ ...editVenue, name: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="venue-location">Location</Label>
                <Input
                  id="venue-location"
                  value={editVenue.location}
                  onChange={(e) =>
                    setEditVenue({ ...editVenue, location: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="venue-price">{getPriceLabel(editVenue.type)}</Label>
                <Input
                  id="venue-price"
                  type="number"
                  min={getPriceStep(editVenue.type)}
                  step={getPriceStep(editVenue.type)}
                  value={editVenue.basePrice}
                  onChange={(e) =>
                    setEditVenue({
                      ...editVenue,
                      basePrice: Number(e.target.value),
                    })
                  }
                />
                <p className="mt-1 text-xs text-gray-500">{getPriceHelpText(editVenue.type)}</p>
              </div>
              <div>
                <Label htmlFor="venue-desc">Description</Label>
                <Textarea
                  id="venue-desc"
                  value={editVenue.description ?? ""}
                  onChange={(e) =>
                    setEditVenue({ ...editVenue, description: e.target.value })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVenue(null)}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent aria-describedby="delete-venue-desc">
          <DialogHeader>
            <DialogTitle>Delete Venue</DialogTitle>
            <DialogDescription id="delete-venue-desc">
              Are you sure? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
